import { createRouter, createWebHistory, START_LOCATION } from 'vue-router';
import routes from './routes';
import {redirectOwnProfileToMyPages} from '~/angular/app/userprofile_page/routes';
import { isBuildStale, isChunkLoadError, reloadInto } from 'shared/staleBuild';

const router = createRouter({
    // vue-router 4 takes a history implementation rather than `mode: 'history'`
    history: createWebHistory(),
    routes,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        // only the sections whose own router reset the scroll do; the rest never
        // had a scrollBehavior and kept whatever position the browser left them at
        const section = to.matched[0];
        if (section && section.meta && section.meta.resetScroll) {
            return {x: 0, y: 0};
        }
        return undefined;
    }
});

// Ahead of the app's own guards: a bundle the server has replaced should not be deciding
// where the user goes, and the chunk the route needs may not be on the server any more.
// Skipped for the navigation that mounts the app, which this document is already committed to.
router.beforeEach((to, from) => {
    if (from === START_LOCATION || !isBuildStale()) {
        return true;
    }
    return !reloadInto(to.fullPath);
});

router.beforeEach(redirectOwnProfileToMyPages);

// The same problem arriving from the other side - the build is current as far as anyone
// knows, but a chunk it asks for is gone, so this route can never render here. The url is
// fine; a fresh document renders it.
router.onError((error, to) => {
    if (isChunkLoadError(error) && to) {
        reloadInto(to.fullPath);
    }
});

// router 3 rejected a navigation to the current route with a NavigationDuplicated error,
// which the app used to silence by wrapping push/replace (vue/VueRouterSilenceDuplicationError).
// Router 4 resolves such a navigation instead of rejecting, and reports the reason as a
// failure object, so the wrapper is gone - but push() still rejects on a guard that throws,
// and those rejections were being swallowed too. Keep swallowing only the redundant ones.
for (const method of ['push', 'replace']) {
    const original = router[method].bind(router);
    router[method] = function (location) {
        return original(location).catch(err => {
            if (err && err.name === 'NavigationDuplicated') {
                return undefined;
            }
            throw err;
        });
    };
}

export default router;
